import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// 异步actions
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', credentials);
      const { token, refreshToken, user } = response.data;
      
      // 存储token和用户信息
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(user));
      
      return { token, refreshToken, user };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '登录失败');
    }
  }
);

// 管理员登录
export const adminLogin = createAsyncThunk(
  'auth/adminLogin',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/admin/login', credentials);
      const { token, refreshToken, admin, needGa, loginTicket } = response.data;

      // 进入两步验证（Google验证器），暂不登录
      if (needGa) {
        return { needGa, loginTicket };
      }

      // 存储token和管理员信息
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('adminInfo', JSON.stringify(admin));
      localStorage.setItem('userType', 'admin');
      
      return { token, refreshToken, admin };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '登录失败');
    }
  }
);

// 管理员登录第二步（Google验证器动态码）
export const adminGaLogin = createAsyncThunk(
  'auth/adminGaLogin',
  async ({ loginTicket, gaCode }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/admin/login/ga', { loginTicket, gaCode });
      const { token, refreshToken, admin } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('adminInfo', JSON.stringify(admin));
      localStorage.setItem('userType', 'admin');

      return { token, refreshToken, admin };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Google验证码校验失败');
    }
  }
);

// 获取图形验证码
export const getCaptcha = createAsyncThunk(
  'auth/getCaptcha',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/common/captcha');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '获取验证码失败');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // 即使请求失败也要清除本地存储
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      localStorage.removeItem('adminInfo');
      localStorage.removeItem('userType');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const response = await api.post('/auth/refresh', { refreshToken });
      const { token, refreshToken: newRefreshToken } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', newRefreshToken);
      
      return { token, refreshToken: newRefreshToken };
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userInfo');
      return rejectWithValue(error.response?.data?.message || 'Token刷新失败');
    }
  }
);

export const sendVerificationCode = createAsyncThunk(
  'auth/sendVerificationCode',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/verification-codes', params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '发送验证码失败');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (params, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/reset-password', params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || '重置密码失败');
    }
  }
);

// 初始状态
const initialState = {
  user: JSON.parse(localStorage.getItem('userInfo')) || null,
  admin: JSON.parse(localStorage.getItem('adminInfo')) || null,
  token: localStorage.getItem('token') || null,
  refreshToken: localStorage.getItem('refreshToken') || null,
  isAuthenticated: !!localStorage.getItem('token'),
  isLoading: false,
  error: null,
  captcha: {
    captchaId: null,
    captchaImage: null,
    isLoading: false,
    error: null
  }
};

// slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
      localStorage.setItem('userInfo', JSON.stringify(state.user));
    },
  },
  extraReducers: (builder) => {
    builder
      // 登录
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      // 管理员登录
      .addCase(adminLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        // 需要两步验证时，暂不进入登录态
        if (action.payload.needGa) {
          return;
        }
        state.isAuthenticated = true;
        state.admin = action.payload.admin;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      // 管理员Google验证码登录
      .addCase(adminGaLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminGaLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.admin = action.payload.admin;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.error = null;
      })
      .addCase(adminGaLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload;
      })
      // 获取图形验证码
      .addCase(getCaptcha.pending, (state) => {
        state.captcha.isLoading = true;
        state.captcha.error = null;
      })
      .addCase(getCaptcha.fulfilled, (state, action) => {
        state.captcha.isLoading = false;
        state.captcha.captchaId = action.payload.captchaId;
        state.captcha.captchaImage = action.payload.captchaImage;
        state.captcha.error = null;
      })
      .addCase(getCaptcha.rejected, (state, action) => {
        state.captcha.isLoading = false;
        state.captcha.error = action.payload;
      })
      // 登出
      .addCase(logout.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.admin = null;
        state.token = null;
        state.refreshToken = null;
        state.error = null;
      })
      // 刷新token
      .addCase(refreshToken.fulfilled, (state, action) => {
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.refreshToken = null;
      })
      // 发送验证码
      .addCase(sendVerificationCode.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendVerificationCode.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(sendVerificationCode.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // 重置密码
      .addCase(resetPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;
